import React from 'react';
import { Link } from 'react-router-dom';

interface Article {
  id: string;
  title: string;
  excerpt: string;
  slug?: string;
  featuredImage?: string;
  category?: { name: string };
  tag?: string;
  createdAt?: string;
  date?: string;
  link?: string;
}

interface RelatedArticlesProps {
  currentArticle: Article;
  allArticles: Article[];
  maxArticles?: number;
  className?: string;
}

const RelatedArticles: React.FC<RelatedArticlesProps> = ({
  currentArticle,
  allArticles,
  maxArticles = 3,
  className = ''
}) => {
  // Funzione per calcolare la similarità tra articoli
  const calculateSimilarity = (article1: Article, article2: Article): number => {
    let score = 0;
    
    // Stesso categoria/tag = +3 punti
    const isGraphQL1 = !!article1.createdAt && !article1.date;
    const isGraphQL2 = !!article2.createdAt && !article2.date;
    
    const category1 = isGraphQL1 ? (article1.category?.name || 'Generale') : article1.tag;
    const category2 = isGraphQL2 ? (article2.category?.name || 'Generale') : article2.tag;
    
    if (category1 === category2) {
      score += 3;
    }
    
    // Parole comuni nel titolo = +1 punto per parola
    const words1 = article1.title.toLowerCase().split(' ').filter(word => word.length > 3);
    const words2 = article2.title.toLowerCase().split(' ').filter(word => word.length > 3);
    const commonWords = words1.filter(word => words2.includes(word));
    score += commonWords.length;
    
    // Parole comuni nell'excerpt = +0.5 punti per parola
    const excerptWords1 = article1.excerpt.toLowerCase().split(' ').filter(word => word.length > 3);
    const excerptWords2 = article2.excerpt.toLowerCase().split(' ').filter(word => word.length > 3);
    const commonExcerptWords = excerptWords1.filter(word => excerptWords2.includes(word));
    score += commonExcerptWords.length * 0.5;
    
    return score;
  };

  // Trova articoli correlati
  const relatedArticles = allArticles
    .filter(article => article.id !== currentArticle.id) // Escludi l'articolo corrente
    .map(article => ({
      ...article,
      similarity: calculateSimilarity(currentArticle, article)
    }))
    .sort((a, b) => b.similarity - a.similarity) // Ordina per similarità
    .slice(0, maxArticles); // Prendi solo i primi N

  // Se non ci sono abbastanza articoli correlati, non mostrare la sezione
  if (relatedArticles.length === 0) {
    return null;
  }

  return (
    <div className={`bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 ${className}`}>
      <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-6 flex items-center">
        <svg className="w-5 h-5 mr-2 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
        </svg>
        Articoli Correlati
      </h3>
      
      <div className="space-y-4">
        {relatedArticles.map((article) => {
          const isGraphQLArticle = !!article.createdAt && !article.date;
          const displayImage = isGraphQLArticle 
            ? (article.featuredImage || '/images/logo-unasca.png')
            : '/images/logo-unasca.png';
          
          const displayLink = isGraphQLArticle
            ? `/blog/${article.slug || article.id}`
            : article.link;
          
          const displayTag = isGraphQLArticle 
            ? (article.category?.name || 'Generale')
            : article.tag;

          const displayDate = isGraphQLArticle
            ? new Date(article.createdAt!).toLocaleDateString('it-IT')
            : new Date(article.date!).toLocaleDateString('it-IT');

          return (
            <Link
              key={article.id}
              to={displayLink || '#'}
              className="flex items-start space-x-4 p-4 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors group"
            >
              {/* Immagine */}
              <div className="flex-shrink-0">
                <img
                  src={displayImage}
                  alt={article.title}
                  className="w-16 h-16 object-cover rounded-lg"
                  onError={(e) => {
                    e.currentTarget.src = '/images/logo-unasca.png';
                  }}
                />
              </div>
              
              {/* Contenuto */}
              <div className="flex-1 min-w-0">
                <h4 className="text-sm font-medium text-gray-900 dark:text-white group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors line-clamp-2 mb-1">
                  {article.title}
                </h4>
                <p className="text-xs text-gray-600 dark:text-gray-300 line-clamp-2 mb-2">
                  {article.excerpt}
                </p>
                <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                  <span className="flex items-center">
                    <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    {displayDate}
                  </span>
                  <span className="bg-primary-100 dark:bg-primary-900 text-primary-600 dark:text-primary-300 px-2 py-1 rounded-full text-xs">
                    {displayTag}
                  </span>
                </div>
              </div>
              
              {/* Freccia */}
              <div className="flex-shrink-0">
                <svg className="w-4 h-4 text-gray-400 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </Link>
          );
        })}
      </div>
      
      {/* Link per vedere tutti gli articoli */}
      <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-600">
        <Link
          to="/blog"
          className="inline-flex items-center text-sm font-medium text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 transition-colors"
        >
          Vedi tutti gli articoli
          <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </Link>
      </div>
    </div>
  );
};

export default RelatedArticles;
